import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box, Card, Typography, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Stack, Divider, Button, Link, Tooltip, CircularProgress, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { PageHeader } from "../../components/PageHeader.js";
import { EmptyState } from "../../components/EmptyState.js";
import { MessageStatusChip } from "../../components/StatusChip.js";
import { useApi } from "../../hooks/useApi.js";
import { useWhatsappEvents } from "../../hooks/useWhatsappEvents.js";
import { ui } from "../../store/uiStore.js";
import { whatsappErrorLabel } from "../../lib/whatsappErrors.js";
import { useTranslation } from "react-i18next";
import type { WhatsAppMessage, PartnerReduced } from "@kappa-v2/contracts";
import { format } from "date-fns";

// Stati reali = chiavi i18n form.whatsapp.* (etichetta filtro tramite i18n)
const STATUSES = [
  { key: "form.whatsapp.sent", labelKey: "whatsapp.messages.filterSent" },
  { key: "form.whatsapp.delivered", labelKey: "whatsapp.messages.filterDelivered" },
  { key: "form.whatsapp.read", labelKey: "whatsapp.messages.filterRead" },
  { key: "form.whatsapp.pending", labelKey: "whatsapp.messages.filterPending" },
  { key: "form.whatsapp.failed", labelKey: "whatsapp.messages.filterFailed" }
];
const shortStatus = (s?: string) => s?.split(".").pop() ?? "";
const ts = (m: WhatsAppMessage) => new Date(m.sentAt ?? m.createdAt ?? 0).getTime();

interface Group {
  queueId: string;
  partnerId: string;
  cellPhone: string;
  fullName: string;
  messages: WhatsAppMessage[];
  lastAt: number;
}

export function WhatsAppMessagesPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const [rows, setRows] = useState<WhatsAppMessage[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [resending, setResending] = useState<Set<string>>(new Set());
  const [viewMsg, setViewMsg] = useState<WhatsAppMessage | null>(null);
  const [openErr, setOpenErr] = useState<string | null>(null);
  // Conteggi REALI per stato (dal BE), indipendenti dalle righe caricate in pagina.
  const [counts, setCounts] = useState<{ total: number; byStatus: Record<string, number> }>({ total: 0, byStatus: {} });

  const fetchStats = useCallback(() => {
    call<{ total: number; byStatus: Record<string, number> }>("/api/v1/whatsapp/messages/stats", { silent: true, noLoader: true })
      .then((r) => setCounts({ total: r?.total ?? 0, byStatus: r?.byStatus ?? {} }))
      .catch(() => { /* lascia i conteggi precedenti */ });
  }, [call]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const PAGE_INITIAL = 100;
  const PAGE_MORE = 20;
  const skipRef = useRef(0);
  const statusRef = useRef("");
  const loadingMoreRef = useRef(false);

  // Caricamento paginato (infinite scroll): prima pagina di 100 in base ai filtri,
  // poi 20 alla volta scrollando. Lo stato è filtrato lato server; la ricerca
  // testuale resta lato client sulle righe già caricate.
  const fetchPage = useCallback(async (reset: boolean, silent = false) => {
    if (reset) { if (!silent) setLoading(true); skipRef.current = 0; }
    else { if (loadingMoreRef.current) return; loadingMoreRef.current = true; setLoadingMore(true); }
    const params = new URLSearchParams();
    if (statusRef.current) params.set("status", statusRef.current);
    params.set("limit", String(reset ? PAGE_INITIAL : PAGE_MORE));
    params.set("skip", String(skipRef.current));
    try {
      const r = await call<{ data: WhatsAppMessage[]; total?: number } | WhatsAppMessage[]>(
        `/api/v1/whatsapp/messages?${params.toString()}`, { silent: true, noLoader: true }
      );
      const data = Array.isArray(r) ? r : (r.data ?? []);
      const tot = Array.isArray(r) ? data.length : (r.total ?? data.length);
      skipRef.current += data.length;
      setRows((prev) => (reset ? data : [...prev, ...data]));
      setTotal(tot);
      setHasMore(skipRef.current < tot);
    } catch {
      if (reset) { setRows([]); setTotal(0); setHasMore(false); }
    } finally {
      if (reset) setLoading(false);
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [call]);

  // (Ri)carica la prima pagina al mount e ad ogni cambio del filtro di stato.
  useEffect(() => { statusRef.current = statusFilter; void fetchPage(true); }, [statusFilter, fetchPage]);

  // Aggiornamento realtime: ricarica la prima pagina (silenzioso) → nuovi in cima.
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useWhatsappEvents(() => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
    refetchTimer.current = setTimeout(() => { void fetchPage(true, true); fetchStats(); }, 600);
  });

  // Sentinella per l'infinite scroll: quando entra nel viewport carica i prossimi 20.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting && hasMore && !loadingMoreRef.current) void fetchPage(false); },
      { rootMargin: "400px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, fetchPage]);

  // Anagrafica ridotta per risolvere partnerId → nominativo.
  useEffect(() => {
    call<{ data?: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all", { silent: true, noLoader: true })
      .then((r) => {
        const list = Array.isArray(r) ? r : (r.data ?? []);
        setNames(Object.fromEntries(list.map((p) => [String(p._id), p.fullName])));
      })
      .catch(() => setNames({}));
  }, [call]);

  const cnt = (k: string) => counts.byStatus[k] ?? 0;

  // Raggruppa per invio (queueId), ordina i gruppi dal più recente.
  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();
    for (const m of rows) {
      const key = m.queueId || m._id || "";
      let g = map.get(key);
      if (!g) {
        g = { queueId: m.queueId, partnerId: m.partnerId, cellPhone: m.cellPhone, fullName: "", messages: [], lastAt: 0 };
        map.set(key, g);
      }
      g.messages.push(m);
      g.lastAt = Math.max(g.lastAt, ts(m));
    }
    const out = Array.from(map.values());
    for (const g of out) {
      g.fullName = names[g.partnerId] || "";
      g.messages.sort((a, b) => ts(a) - ts(b)); // testo prima, poi allegati
    }
    return out.sort((a, b) => b.lastAt - a.lastAt);
  }, [rows, names]);

  // Filtri a livello di gruppo.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (statusFilter && !g.messages.some((m) => m.status === statusFilter)) return false;
      if (!q) return true;
      const hay = `${g.fullName} ${g.cellPhone} ${g.messages.map((m) => m.message ?? "").join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [groups, search, statusFilter]);

  const resend = async (g: Group) => {
    if (!g.queueId) { ui.toast(t("whatsapp.messages.resendNoQueue"), "error"); return; }
    setResending((s) => new Set(s).add(g.queueId));
    try {
      const r = await call<{ sent?: number; failed?: number; blocked?: number; waitingWindow?: number; running?: boolean }>(
        `/api/v1/whatsapp/queues/${g.queueId}/resend`, { method: "POST", silent: true }
      );
      if (r?.running) {
        ui.toast(t("whatsapp.messages.resendRunning"), "info");
      } else if ((r?.waitingWindow ?? 0) > 0 && !(r?.sent ?? 0)) {
        ui.toast(t("whatsapp.messages.resendWindow"), "info");
      } else {
        ui.toast(t("whatsapp.messages.resendDone", { sent: r?.sent ?? 0 }), "success");
      }
      void fetchPage(true, true);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      ui.toast(status === 404 ? t("whatsapp.messages.resendNoQueue") : t("common.error"), "error");
    } finally {
      setResending((s) => { const n = new Set(s); n.delete(g.queueId); return n; });
    }
  };

  // Riapre un allegato SEMPRE (anche dopo la scadenza dell'URL pubblico): chiede ad
  // api-files un URL firmato per il fileKey (autenticato) e lo apre. Fallback all'URL
  // pubblico se il firmato non è disponibile.
  const openAttachment = useCallback(async (f: { url?: string; downloadUrl?: string }) => {
    const fallback = () => { if (f.downloadUrl) window.open(f.downloadUrl, "_blank", "noopener"); };
    if (!f.url) { fallback(); return; }
    const path = f.url.split("/").map(encodeURIComponent).join("/");
    try {
      const r = await call<{ url?: string }>(`/api/v1/xfiles/reopen/${path}`, { silent: true, noLoader: true });
      if (r?.url) window.open(r.url, "_blank", "noopener");
      else fallback();
    } catch {
      fallback();
    }
  }, [call]);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("whatsapp.messages.title")}
        subtitle={t("whatsapp.messages.subtitle")}
        breadcrumbs={[{ label: t("sidebar.groupCommunications") }, { label: t("sidebar.messagesWa") }]}
      />
      <Box sx={{ p: 3, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }} data-tour="wa-msg-filters">
            <TextField size="small" placeholder={t("whatsapp.messages.searchPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t("common.status")}</InputLabel>
              <Select label={t("common.status")} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="">{t("whatsapp.messages.allCount", { count: counts.total })}</MenuItem>
                {STATUSES.map(s => (
                  <MenuItem key={s.key} value={s.key}>{t(s.labelKey)} ({cnt(s.key)})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="success.main">{t("whatsapp.messages.deliveredSummary", { count: cnt("form.whatsapp.delivered") + cnt("form.whatsapp.read") })}</Typography>
              <Typography variant="caption" color="error.main">{t("whatsapp.messages.failedSummary", { count: cnt("form.whatsapp.failed") })}</Typography>
              <Tooltip title={t("common.refresh")}>
                <IconButton size="small" onClick={() => void fetchPage(true)} aria-label="refresh">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {!loading && filtered.length === 0 ? (
            <EmptyState title={t("whatsapp.messages.none")} description={t("whatsapp.messages.noneDesc")} />
          ) : (
            <Box data-tour="wa-msg-list">
              {filtered.map((g) => (
                <Box key={g.queueId || g.messages[0]?._id}
                  sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, borderBottom: "8px solid #F2F0EA", "&:last-of-type": { borderBottom: "none" } }}>
                  {/* Colonna sinistra: nominativo + cellulare + Reinvia (abbraccia il gruppo) */}
                  <Box sx={{
                    width: { xs: "auto", md: 240 }, flexShrink: 0, p: 2, bgcolor: "#FAFAF7",
                    borderRight: { md: "1px solid rgba(0,0,0,.06)" },
                    borderBottom: { xs: "1px solid rgba(0,0,0,.06)", md: "none" },
                    display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: { xs: "center", md: "stretch" },
                    justifyContent: "space-between", gap: 1
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {g.fullName || t("whatsapp.messages.unknownPartner")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{g.cellPhone}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled">
                      {g.lastAt ? format(new Date(g.lastAt), "dd/MM/yyyy HH:mm") : "—"}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={t("whatsapp.messages.resendHint")}>
                      <span>
                        <Button
                          data-tour="wa-msg-resend"
                          size="small" variant="outlined" color="success"
                          startIcon={resending.has(g.queueId) ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                          disabled={resending.has(g.queueId) || !g.queueId}
                          onClick={() => resend(g)}
                        >
                          {t("whatsapp.messages.resend")}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Colonna destra: righe messaggio separate */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {g.messages.map((m, idx) => (
                      <Box key={m._id ?? idx}>
                        {idx > 0 ? <Divider /> : null}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" color="text.primary" sx={{
                              whiteSpace: "pre-wrap", wordBreak: "break-word",
                              display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden"
                            }}>
                              {m.message || (m.files?.length ? "" : "—")}
                            </Typography>
                            {m.message && m.message.length > 160 && (
                              <Button data-tour="wa-msg-readmore" size="small" onClick={() => setViewMsg(m)} sx={{ px: 0, minWidth: 0, textTransform: "none" }}>
                                {t("whatsapp.messages.readMore")}
                              </Button>
                            )}
                            {(m.files ?? []).map((f, fi) =>
                              (f.url || f.downloadUrl) ? (
                                <Link key={fi} component="button" type="button" onClick={() => void openAttachment(f)}
                                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.8rem", mt: 0.5, mr: 2 }}>
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                  {f.fileName || t("whatsapp.messages.attachment")}
                                </Link>
                              ) : null
                            )}
                          </Box>
                          {m.status === "form.whatsapp.failed" && m.error ? (
                            <span
                              role="button"
                              onClick={() => setOpenErr(openErr === (m._id ?? "") ? null : (m._id ?? ""))}
                              style={{ cursor: "pointer" }}
                              title={t("whatsapp.messages.failureReason")}
                            >
                              <MessageStatusChip status={shortStatus(m.status)} />
                            </span>
                          ) : (
                            <MessageStatusChip status={shortStatus(m.status)} />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ width: 110, textAlign: "right", flexShrink: 0 }}>
                            {m.sentAt ? format(new Date(m.sentAt), "dd/MM/yyyy HH:mm") : "—"}
                          </Typography>
                        </Box>
                        {m.status === "form.whatsapp.failed" && m.error && openErr === (m._id ?? "") ? (
                          <Box sx={{ mx: 2, mb: 1.25, p: 1.25, borderRadius: 1, bgcolor: "error.light", color: "error.contrastText" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                              {t("whatsapp.messages.failureReason")}
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{whatsappErrorLabel(m.error, t)}</Typography>
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
          {loadingMore ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
        </Card>
      </Box>

      <Dialog open={Boolean(viewMsg)} onClose={() => setViewMsg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("whatsapp.messages.fullMessageTitle")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {viewMsg?.message}
          </Typography>
          {(viewMsg?.files ?? []).map((f, fi) =>
            (f.url || f.downloadUrl) ? (
              <Link key={fi} component="button" type="button" onClick={() => void openAttachment(f)}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.85rem", mt: 1.5, mr: 2 }}>
                <DownloadIcon sx={{ fontSize: 18 }} />
                {f.fileName || t("whatsapp.messages.attachment")}
              </Link>
            ) : null
          )}
          {viewMsg?.status === "form.whatsapp.failed" && viewMsg?.error ? (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: "error.light", color: "error.contrastText" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                {t("whatsapp.messages.failureReason")}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{whatsappErrorLabel(viewMsg.error, t)}</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMsg(null)}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
